import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Zap, Trophy, XCircle, BellRing, Star, Check, AlertCircle } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { LoadingRows, ErrorState, EmptyState } from "@/components/common/States";
import { IntelligenceCard } from "@/components/cards/Cards";
import { MarketBadge } from "@/components/badges/Badges";
import { useFeed } from "@/hooks/useAtlas";
import { lifecycleOf } from "@/presentation/resultPresentation";
import { timeAgo } from "@/lib/format";
import { useFollow, NOTIF_EVENTS, NOTIF_LABELS } from "@/features/follow/FollowContext";
import { useI18n } from "@/i18n";

type Cat = "ALL" | "WIN" | "LOSE" | "SIGNAL";

export default function Notifications() {
  const { d, lang } = useI18n();
  const { data, isLoading, isError, error, refetch } = useFeed(40);
  const { followed, unfollow, prefs, setPref, permission, requestPermission } = useFollow();
  const [cat, setCat] = useState<Cat>("ALL");

  const items = useMemo(() => {
    const rows = data?.rows || [];
    return rows.map((r) => {
      const key = lifecycleOf(r.status, r.won);
      const c: Cat = key === "WIN" ? "WIN" : key === "LOSE" ? "LOSE" : "SIGNAL";
      return { ...r, cat: c };
    }).filter((r) => cat === "ALL" || r.cat === cat);
  }, [data, cat]);

  const meta = (c: Cat) => c === "WIN"
    ? { icon: Trophy, color: "text-atlas-400 bg-atlas/10", label: lang === "tr" ? "Sinyal kazandı" : "Signal won", surf: "border-atlas/30 bg-atlas/[0.05]", accent: "bg-atlas" }
    : c === "LOSE" ? { icon: XCircle, color: "text-live-400 bg-live/10", label: lang === "tr" ? "Sinyal kaybetti" : "Signal lost", surf: "border-live/25 bg-live/[0.045]", accent: "bg-live" }
    : { icon: Zap, color: "text-gold-400 bg-gold/10", label: lang === "tr" ? "Yeni sinyal" : "New signal", surf: "", accent: "bg-gold" };

  const tabs: Cat[] = ["ALL", "SIGNAL", "WIN", "LOSE"];
  const tabLabel = (t: Cat) => t === "ALL" ? d.common.all : t === "SIGNAL" ? (lang === "tr" ? "Sinyaller" : "Signals") : t === "WIN" ? d.result.win : d.result.lose;

  const permBadge = permission === "granted"
    ? { tone: "border-atlas/30 bg-atlas/10 text-atlas-300", label: d.notifPrefs.enabled, icon: <Check className="h-3.5 w-3.5" /> }
    : permission === "denied" ? { tone: "border-live/25 bg-live/10 text-live-400", label: d.notifPrefs.denied, icon: <XCircle className="h-3.5 w-3.5" /> }
    : permission === "unsupported" ? { tone: "border-white/10 bg-white/5 text-muted-foreground", label: d.notifPrefs.unsupported, icon: <AlertCircle className="h-3.5 w-3.5" /> }
    : { tone: "border-gold/25 bg-gold/10 text-gold-300", label: d.notifPrefs.permissionRequired, icon: <AlertCircle className="h-3.5 w-3.5" /> };

  return (
    <div data-testid="notifications-page">
      <PageHeader title={d.nav.notifications}
        subtitle={lang === "tr" ? "Takip tercihlerini yönet, canlı fırsatları ve sonuçları gör." : "Manage your follow preferences and see live opportunities & results." } />

      <div className="grid gap-6 xl:grid-cols-[1fr_1.4fr]">
        {/* preferences */}
        <div className="min-w-0 space-y-6">
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><BellRing className="h-4 w-4 text-atlas-400" /> {d.notifPrefs.title}</span>}>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] p-3" data-testid="notif-permission">
              <div><p className="text-sm font-semibold text-foreground">{d.notifPrefs.browser}</p>
                <span className={`mt-1.5 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${permBadge.tone}`}>{permBadge.icon}{permBadge.label}</span>
              </div>
              {(permission === "default") && (
                <button onClick={requestPermission} data-testid="notif-enable-btn" className="shrink-0 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-3.5 py-2 text-xs font-bold text-atlas-ink transition hover:shadow-glow">{d.notifPrefs.enable}</button>
              )}
            </div>

            <p className="mt-4 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{d.notifPrefs.events}</p>
            <div className="space-y-1.5" data-testid="notif-events">
              {NOTIF_EVENTS.map((k) => (
                <label key={k} className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-2 transition hover:bg-white/[0.03]" data-testid={`notif-event-${k}`}>
                  <span className="text-sm text-foreground/90">{NOTIF_LABELS[k][lang]}</span>
                  <button type="button" onClick={() => setPref(k, !prefs[k])} role="switch" aria-checked={prefs[k]}
                    className={`relative h-5 w-9 shrink-0 rounded-full transition ${prefs[k] ? "bg-atlas-500" : "bg-white/10"}`}>
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${prefs[k] ? "left-[18px]" : "left-0.5"}`} />
                  </button>
                </label>
              ))}
            </div>
          </IntelligenceCard>

          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Star className="h-4 w-4 text-gold-400" /> {d.notifPrefs.followedTitle}</span>}>
            {followed.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">{d.notifPrefs.noFollowed}</p>
              : (
                <div className="space-y-2" data-testid="followed-list">
                  {followed.map((m) => (
                    <div key={m.fixtureId} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5" data-testid="followed-row">
                      <Link to={`/app/match/${m.fixtureId}`} className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-foreground transition hover:text-atlas-300">{m.label}</p></Link>
                      <button onClick={() => unfollow(m.fixtureId)} data-testid={`unfollow-${m.fixtureId}`} className="shrink-0 rounded-lg border border-white/10 px-2 py-1 text-[11px] font-semibold text-muted-foreground transition hover:border-live/30 hover:text-live-400">{lang === "tr" ? "Bırak" : "Unfollow"}</button>
                    </div>
                  ))}
                </div>
              )}
          </IntelligenceCard>
        </div>

        {/* activity */}
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap gap-1 rounded-xl border border-white/10 bg-white/[0.03] p-1" data-testid="notif-tabs">
            {tabs.map((t) => (
              <button key={t} onClick={() => setCat(t)} className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${cat === t ? "bg-atlas/15 text-atlas-300" : "text-muted-foreground hover:text-foreground"}`}>
                {tabLabel(t)}
              </button>
            ))}
          </div>

          {isLoading ? <LoadingRows count={6} /> : isError ? <ErrorState error={error as any} onRetry={refetch} />
            : items.length === 0 ? <EmptyState title={d.states.emptyNotifications} icon={<Bell className="h-6 w-6" />} />
            : (
              <div className="space-y-2" data-testid="notif-list">
                {items.map((r, i) => {
                  const m = meta(r.cat);
                  return (
                    <Link key={i} to={`/app/match/${r.fixture_id}`} className={`panel panel-hover relative flex items-center gap-3 overflow-hidden p-4 pl-5 ${m.surf}`} data-testid="notif-row">
                      <span className={`absolute inset-y-0 left-0 w-1 ${m.accent}`} aria-hidden />
                      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${m.color}`}><m.icon className="h-5 w-5" /></span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-foreground">{m.label}</p>
                        <p className="truncate text-xs text-muted-foreground">{r.match}</p>
                      </div>
                      <div className="flex min-w-0 max-w-[42%] flex-col items-end gap-1.5 text-right">
                        <span className="max-w-full truncate"><MarketBadge code={r.market} size="sm" /></span>
                        <span className="text-[11px] text-muted-foreground">{timeAgo(r.time, lang)}</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
